/**
 * Inbound documents handed to markbird from outside the app.
 *
 * An external tool (the browser extension on claude.ai, the MCP server used by
 * Claude Desktop / Claude Code) opens
 *
 *     https://app.markbird.md/new#<payload>
 *
 * and markbird turns that payload into a document in the local library.
 *
 * The payload lives in the URL **fragment** on purpose. Fragments are never sent
 * to the server — they stay out of Cloud Run access logs, out of the `Referer`
 * header, and off the network entirely. That keeps a feature whose whole job is
 * to move your writing around from quietly becoming the one path that ships your
 * notes to a server, which would contradict the local-first promise the rest of
 * the app makes. It also means this works with the API down and fully offline.
 *
 * Payload format: base64url of gzip of the UTF-8 JSON `{ title?, content }`.
 * Gzip matters because Markdown compresses roughly 3-4x, which is the difference
 * between a long document fitting in a URL and being truncated by a browser.
 */

import type { MarkdownDocument } from '@markbird/shared';
import { createDocument, setLastOpenDocId } from './library';

/** A document handed to markbird from outside. */
export interface InboundDoc {
  /** Optional; when absent the title is derived from the content as usual. */
  title?: string;
  content: string;
}

const HASH_PREFIX = '#';

/**
 * Byte buffers pinned to a plain `ArrayBuffer`. The default `Uint8Array` is
 * generic over `ArrayBufferLike`, which includes `SharedArrayBuffer` and so is
 * not assignable to the `BufferSource` that the stream writer wants.
 */
type Bytes = Uint8Array<ArrayBuffer>;

/** base64url (RFC 4648 §5) — URL-safe, and no `=` padding to waste characters. */
function toBase64Url(bytes: Bytes): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): Bytes {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Push bytes through a compression stream and collect the result.
 *
 * Deliberately avoids `new Blob([...]).stream()` and `new Response(...)`: jsdom
 * ships neither `Blob.stream()` nor a streaming `Response`, so the convenient
 * one-liner version cannot be unit-tested. Driving the reader by hand needs only
 * `CompressionStream` and `ReadableStream`, which both the browser and the test
 * environment have.
 */
async function through(bytes: Bytes, stream: TransformStream<BufferSource, Bytes>): Promise<Bytes> {
  const writer = stream.writable.getWriter();
  // When the transform errors — which is the normal case for a corrupt payload
  // — *both* ends reject. The reader rejection below is the one we want, since
  // it propagates to the caller's try/catch; swallowing the writer's twin here
  // keeps that failure from also surfacing as an unhandled rejection.
  const written = writer.write(bytes).then(() => writer.close());
  written.catch(() => {});

  const reader = stream.readable.getReader();
  const chunks: Bytes[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
  }

  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

const gzip = (bytes: Bytes): Promise<Bytes> => through(bytes, new CompressionStream('gzip'));

const gunzip = (bytes: Bytes): Promise<Bytes> => through(bytes, new DecompressionStream('gzip'));

/**
 * Encode a document into the fragment payload.
 *
 * Exported because the extension and the MCP server must produce byte-identical
 * payloads — keeping the encoder here, next to the decoder, is what stops the
 * two ends from drifting apart.
 */
export async function encodeInboundDoc(doc: InboundDoc): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(doc));
  return toBase64Url(await gzip(json));
}

/**
 * Decode a fragment payload. Returns `null` for anything malformed rather than
 * throwing: the payload arrives from outside the app, so a truncated or hand-
 * edited URL is an expected input, not an exceptional one, and it must not take
 * the editor down on boot.
 */
export async function decodeInboundDoc(payload: string): Promise<InboundDoc | null> {
  try {
    const json = new TextDecoder().decode(await gunzip(fromBase64Url(payload)));
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { title, content } = parsed as Partial<InboundDoc>;
    if (typeof content !== 'string') return null;
    return typeof title === 'string' && title.length > 0 ? { title, content } : { content };
  } catch {
    return null;
  }
}

let pendingPayload: string | null = null;

/**
 * Read the pending inbound document out of the URL, if this is a `/new` launch,
 * and strip it from the address bar.
 *
 * Call this once from the entry point, *before* React mounts. Two reasons it
 * cannot wait for an effect: the router would otherwise see `/new` and a later
 * `replaceState` would desync its internal location, and — more importantly —
 * the payload has to be off the URL before anything can reload. If the fragment
 * survived, every refresh would re-import the same document and the library
 * would fill up with copies. Rewriting also keeps the (potentially very long)
 * payload out of the back/forward history and out of anything that later reads
 * `location.href`, such as a share link.
 */
export function captureInboundPayload(): void {
  if (typeof window === 'undefined') return;
  const { pathname, hash } = window.location;
  if (pathname !== '/new') return;

  const payload = hash.startsWith(HASH_PREFIX) ? hash.slice(HASH_PREFIX.length) : '';
  pendingPayload = payload.length > 0 ? payload : null;
  // Rewrite to `/` even when the payload is empty or junk, so a failed import
  // leaves the user on the normal editor URL rather than a dead `/new`.
  window.history.replaceState(null, '', '/');
}

/**
 * Turn a captured payload into a real document, or return `null` when this was
 * an ordinary launch.
 *
 * Clears the pending payload as it goes, so React's StrictMode double-invoking
 * the bootstrap effect in development imports the document once, not twice.
 */
export async function consumeInboundDoc(): Promise<MarkdownDocument | null> {
  const payload = pendingPayload;
  pendingPayload = null;
  if (!payload) return null;

  const inbound = await decodeInboundDoc(payload);
  if (!inbound) return null;

  const doc = await createDocument(inbound);
  await setLastOpenDocId(doc.id);
  return doc;
}
